import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Legal.css';

const RefundPolicy = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('support@crackit.si');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="legal-page-container">
      <div className="legal-content-wrapper">
        
        {/* Navigation Bar */}
        <div className="legal-nav-header">
          <div className="legal-nav-left">
            <button className="legal-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>BACK</span>
            </button>
            <div className="legal-breadcrumb-badge">
              <span>CRACKIT AI</span>
              <span className="crumb-separator">/</span>
              <span>LEGAL PROTOCOLS</span>
              <span className="crumb-separator">/</span>
              <span className="crumb-current">REFUND POLICY</span>
            </div>
          </div>

          <div className="legal-tabs-switch">
            <Link to="/terms" className="legal-tab-link">Terms of Service</Link>
            <Link to="/privacy" className="legal-tab-link">Privacy Policy</Link>
            <Link to="/refund-policy" className="legal-tab-link active-legal-tab">Refund Policy</Link>
          </div>
        </div>

        {/* Header Banner */}
        <motion.div 
          className="legal-header-card danger-header"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <span className="legal-category-badge badge-danger-pill">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Billing & Cancellation Protocol
          </span>
          <h1 className="legal-main-title">REFUND POLICY</h1>
          
          <div className="legal-meta-row">
            <span className="legal-meta-pill danger-pill">
              <strong>Status:</strong> Strict No-Refund Policy
            </span>
            <span className="legal-meta-pill">
              <strong>Effective:</strong> September 19, 2026
            </span>
            <span className="legal-meta-pill">
              <strong>Gateway:</strong> Razorpay Verified
            </span>
            <span className="legal-meta-pill">
              <strong>Support:</strong> support@crackit.si
            </span>
          </div>
        </motion.div>

        {/* Bento Key Facts Grid */}
        <div className="legal-highlights-grid">
          <div className="highlight-stat-card">
            <div className="highlight-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h3 className="highlight-card-title">Instant Refill</h3>
            <p className="highlight-card-desc">Credits deposit into your balance immediately upon Razorpay confirmation.</p>
          </div>

          <div className="highlight-stat-card">
            <div className="highlight-icon-box green-glow">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                <line x1="6" y1="6" x2="6.01" y2="6"/>
                <line x1="6" y1="18" x2="6.01" y2="18"/>
              </svg>
            </div>
            <h3 className="highlight-card-title">GPU Dedicated</h3>
            <p className="highlight-card-desc">Inference capacity (GPT-4o, Claude 3.5, Gemini 1.5 Pro) reserved on purchase.</p>
          </div>

          <div className="highlight-stat-card">
            <div className="highlight-icon-box green-glow">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 14 14"/>
              </svg>
            </div>
            <h3 className="highlight-card-title">Never Expire</h3>
            <p className="highlight-card-desc">Utility credits remain attached to your account indefinitely with zero expiration.</p>
          </div>

          <div className="highlight-stat-card">
            <div className="highlight-icon-box red-glow">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <h3 className="highlight-card-title">Zero Auto-Debits</h3>
            <p className="highlight-card-desc">Pure pay-as-you-go model. We will never enroll you in recurring subscriptions.</p>
          </div>
        </div>

        {/* Main Document Body */}
        <motion.div 
          className="legal-body-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          
          {/* Main Strict No Refund Warning Box */}
          <div className="legal-callout-box callout-danger">
            <div className="callout-icon-wrapper">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
            </div>
            <div className="callout-body">
              <h4>STRICT NO-REFUND POLICY PROTOCOL</h4>
              <p>
                <strong>WE DO NOT PROVIDE REFUNDS UNDER ANY CIRCUMSTANCES. ALL PURCHASES AND CREDIT ORDERS MADE ON CRACKIT AI ARE FINAL, COMPLETED, AND NON-REFUNDABLE.</strong>
              </p>
            </div>
          </div>

          {/* Section 1 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">01</span>
              <h2>Digital Goods & Immediate Service Provision</h2>
            </div>
            <p>
              CrackIT AI provides digital, consumable utility credits that grant immediate, real-time access to high-performance AI inference models (OpenAI GPT-4o, Claude 3.5 Sonnet, Gemini 1.5 Pro) and cloud audio transcription compute servers.
            </p>
            <p>
              When you purchase a credit pack on CrackIT AI:
            </p>
            <ul>
              <li><strong>Instant Fulfillment:</strong> Your purchased credits are instantly deposited into your account balance the moment your payment is verified by our payment gateway (Razorpay).</li>
              <li><strong>Irrevocable Compute Allocation:</strong> Because digital compute resources, GPU capacity, and API tokens are reserved and allocated immediately for your account, digital credits cannot be returned, reclaimed, cancelled, or refunded once purchased.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          {/* Section 2 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">02</span>
              <h2>No Partial, Prorated, or Accidental Purchase Refunds</h2>
            </div>
            <p>
              Please review your selected credit tier carefully before completing payment. We do not provide refunds under reasons including, but not limited to:
            </p>

            <div className="scenario-pill-list">
              <div className="scenario-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span>Change of mind / Cancelled interview</span>
              </div>
              <div className="scenario-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span>Subjective hiring / interview outcome</span>
              </div>
              <div className="scenario-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span>Local client / audio hardware mismatch</span>
              </div>
              <div className="scenario-pill">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span>Accidental tier or quantity selection</span>
              </div>
            </div>

            <ul>
              <li><strong>Unused Credit Balances:</strong> Unused credits remain in your balance and <strong>never expire</strong>, but cannot be converted back to fiat money, cash, or transferred to another payment card/bank account.</li>
              <li><strong>Change of Mind:</strong> If you no longer need the tool, cancel an interview, or change your career plans.</li>
              <li><strong>Interview Outcomes:</strong> Dissatisfaction with interview results, interviewer decisions, hiring outcomes, or subjective preferences regarding AI answer styles.</li>
              <li><strong>Local Client or Connectivity Issues:</strong> User-side internet outages, audio hardware misconfigurations, or failure to install the desktop client on an incompatible machine.</li>
              <li><strong>Accidental Orders:</strong> Entering the wrong quantity or selecting a higher credit package.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          {/* Section 3 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">03</span>
              <h2>Try Before You Buy (Free Testing)</h2>
            </div>
            <p>
              To ensure full transparency and complete satisfaction, CrackIT AI provides complimentary trial credits to new accounts and interactive web demos.
            </p>
            <p>
              We strongly advise all users to test speech recognition, verify desktop overlay functionality on their operating system (macOS or Windows), and test latency before choosing to purchase larger credit packages.
            </p>
          </section>

          <div className="legal-divider"></div>

          {/* Section 4 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">04</span>
              <h2>Payment Glitches & Manual Credit Fulfillment</h2>
            </div>
            <p>
              In rare instances where your bank or card was debited by Razorpay, but credits were not reflected on your dashboard balance due to a browser network timeout or server drop:
            </p>

            <div className="resolution-step-box">
              <h4>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Resolution Protocol (Do NOT file duplicate disputes):
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.88rem' }}>
                Send an email directly to <strong>support@crackit.si</strong> with the following details:
              </p>
              <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '0.88rem' }}>
                <li>Your registered CrackIT email address.</li>
                <li>The <strong>Razorpay Payment ID</strong> (e.g., <code>pay_Ox9A2b3c...</code>) from your payment receipt.</li>
                <li>The exact date and amount debited.</li>
              </ol>
            </div>

            <ul>
              <li>Our support team will verify the gateway ledger within <strong>24–48 hours</strong> and manually deposit the full credit quantity directly into your account balance.</li>
              <li><em>Please note: This resolution is provided in the form of manual credit top-up fulfillment, not a monetary refund.</em></li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          {/* Section 5 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">05</span>
              <h2>Chargebacks & Fraudulent Payment Disputes</h2>
            </div>
            <p>
              Initiating an unauthorized chargeback, reversal, or fraudulent dispute with your issuing bank or credit card provider without contacting us first violates these terms.
            </p>
            <p>
              Any account associated with a chargeback or payment dispute will be subject to:
            </p>
            <ul>
              <li>Immediate and permanent account suspension.</li>
              <li>Immediate forfeiture of all existing credit balances.</li>
              <li>Permanent blacklisting of the user's email, IP address, and payment credentials.</li>
              <li>Reporting of chargeback abuse to financial risk and merchant fraud registries.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          {/* Section 6 */}
          <section className="legal-section">
            <div className="section-title-row">
              <span className="section-num-badge">06</span>
              <h2>No Recurring Subscriptions</h2>
            </div>
            <p>
              CrackIT AI strictly operates on a transparent, one-time credit purchase model. We do not enroll users in recurring auto-renewing subscription plans. You will never be charged automatically unless you explicitly initiate and authorize a new checkout.
            </p>
          </section>

          {/* Contact Card */}
          <div className="legal-contact-card">
            <h3>Billing & Payment Support</h3>
            <p>If you have any questions regarding your transaction history or need payment verification assistance:</p>
            
            <div className="contact-action-row">
              <a href="mailto:support@crackit.si" className="legal-email-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect width="20" height="16" x="2" y="4" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                <span>EMAIL SUPPORT</span>
              </a>

              <button className="legal-copy-email-btn" onClick={handleCopyEmail}>
                {copied ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00ff66" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span style={{ color: '#00ff66' }}>COPIED!</span>
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <span>COPY EMAIL</span>
                  </>
                )}
              </button>
            </div>
          </div>

        </motion.div>

      </div>
    </div>
  );
};

export default RefundPolicy;
